import type { MetricType, MonitorStatus } from '@repo/api-codegen';
import type { BadgeVariant, IconNames } from '@repo/foundations';
import type { SecodaThemeShades } from '@repo/theme/types';
import type { ColorNames } from '@repo/theme/utils';
import type { SelectablePropertyItem } from '../components/ItemIcon/types';

type IncidentStatus = 'active' | 'acknowledged' | 'resolved' | 'expired';

export const INCIDENT_ICON_MAPPING: Record<
	IncidentStatus,
	{
		iconName: IconNames;
		iconColor?: ColorNames;
		iconFillColor?: ColorNames;
		name: string;
	}
> = {
	active: {
		iconName: 'circle',
		iconColor: 'icon/warning/default',
		name: 'Open',
	},
	acknowledged: {
		iconName: 'progress50',
		iconColor: 'icon/caution/default',
		name: 'Acknowledged',
	},
	resolved: {
		iconName: 'circleCheckFilled',
		iconFillColor: 'icon/success/default',
		name: 'Resolved',
	},
	expired: {
		iconName: 'circleXFilled',
		iconFillColor: 'icon/primary/disabled',
		name: 'Expired',
	},
};

export const MONITOR_ICON_MAPPING: Record<
	MonitorStatus,
	{
		iconName: IconNames;
		iconColor?: ColorNames;
		iconFillColor?: ColorNames;
		name: string;
		variant: BadgeVariant;
	}
> = {
	normal: {
		iconName: 'circleCheckFilled',
		iconFillColor: 'icon/success/default',
		name: 'Normal',
		variant: 'success',
	},
	error: {
		iconName: 'alertCircleFilled',
		iconFillColor: 'icon/critical/default',
		name: 'Failed',
		variant: 'critical',
	},
	inactive: {
		iconName: 'lineDashed',
		iconColor: 'icon/secondary/default',
		name: 'Inactive',
		variant: 'default',
	},
	incident: {
		iconName: 'alertTriangleFilled',
		iconFillColor: 'icon/warning/default',
		name: 'Incident',
		variant: 'warning',
	},
	pending: {
		iconName: 'clock',
		iconColor: 'icon/info/default',
		name: 'Pending',
		variant: 'info',
	},
	learning: {
		iconName: 'sparkles',
		iconColor: 'icon/ai/default',
		name: 'Learning',
		variant: 'ai',
	},
};

export const MEASUREMENT_TIME_RANGE_OPTIONS = [
	{
		value: '7',
		label: '7 days',
	},
	{
		value: '14',
		label: '14 days',
	},
	{
		value: '30',
		label: '30 days',
	},
];

export const SCHEDULE_OPTIONS: SelectablePropertyItem[] = [
	{
		label: 'Hourly',
		value: '1',
		iconName: 'clock',
	},
	{
		label: 'Every 3 hours',
		value: '3',
		iconName: 'clock',
	},
	{
		label: 'Every 6 hours',
		value: '6',
		iconName: 'clock',
	},
	{
		label: 'Every 12 hours',
		value: '12',
		iconName: 'clock',
	},
	{
		label: 'Daily',
		value: '24',
		iconName: 'clock',
	},
];

export const THRESHOLD_OPTIONS = [
	{
		label: 'Automatic',
		value: 'automatic',
		iconName: 'sparkles' as IconNames,
	},
	{
		label: 'Manual',
		value: 'manual',
		iconName: 'adjustmentsHorizontal' as IconNames,
	},
];

export const BOUNDS_OPTIONS = [
	{
		label: 'Automatic',
		description: 'Alert on unexpected rises or falls',
		value: 'BOTH',
		iconName: 'easeInOutControlPoints' as IconNames,
	},
	{
		label: 'Upper',
		value: 'UPPER',
		description: 'Alert on unexpected rises only',
		iconName: 'easeOutControlPoint' as IconNames,
	},
	{
		label: 'Always increases',
		value: 'MONOTONIC_INCREASE',
		description: 'Alert on unexpected rises and on any fall',
		iconName: 'trendingUp3' as IconNames,
	},
	{
		label: 'Lower',
		description: 'Alert on unexpected falls only',
		value: 'LOWER',
		iconName: 'easeInControlPoint' as IconNames,
	},
	{
		label: 'Always decreases',
		value: 'MONOTONIC_DECREASE',
		description: 'Alert on unexpected falls and on any rise',
		iconName: 'trendingDown3' as IconNames,
	},
];

export const MODEL_OPTIONS = [
	{
		id: 'iqr',
		name: 'IQR',
		description:
			'Finds outliers using interquartile range. Robust against extreme values and ideal for stable metrics.',
		iconName: 'chartHistogram' as IconNames,
	},
	{
		id: 'z_score',
		name: 'Z-Score',
		description:
			'Detects anomalies using standard deviations from the mean. Best for trending or seasonal data.',
		iconName: 'bounceRight' as IconNames,
	},
] as const;

export const MINIMUM_MENU_OPTIONS = [
	{
		label: 'No minimum',
		value: 'null',
	},
	{
		label: 'Below',
		value: 'below',
	},
];

export const MAXIMUM_MENU_OPTIONS = [
	{
		label: 'No maximum',
		value: 'null',
	},
	{
		label: 'Above',
		value: 'above',
	},
];

export const METRIC_TYPE_INFORMATION: Record<
	MetricType,
	{
		label: string;
		iconName: IconNames;
		description: string;
		fill: SecodaThemeShades;
		value: MetricType;
		group: 'table' | 'column' | 'custom' | 'analytics' | 'job';
		canSchedule: boolean;
		isGrouped: boolean;
	}
> = {
	row_count: {
		value: 'row_count',
		label: 'Row count',
		description: 'Tracks the number of rows over time.',
		iconName: 'layoutRows',
		fill: 'icon/emphasis/default',
		group: 'table',
		canSchedule: true,
		isGrouped: false,
	},
	cardinality: {
		value: 'cardinality',
		label: 'Cardinality',
		description: 'Tracks the number of distinct values in a column.',
		iconName: 'columns2',
		fill: 'icon/emphasis/default',
		group: 'column',
		canSchedule: true,
		isGrouped: false,
	},
	freshness: {
		value: 'freshness',
		label: 'Freshness',
		description: 'Tracks the time elapsed since last update.',
		iconName: 'clockPlay',
		fill: 'icon/emphasis/default',
		group: 'table',
		canSchedule: true,
		isGrouped: false,
	},
	max: {
		value: 'max',
		label: 'Maximum',
		description: 'Tracks the maximum of all values in a column.',
		iconName: 'hash',
		fill: 'icon/emphasis/default',
		group: 'column',
		canSchedule: true,
		isGrouped: false,
	},
	mean: {
		value: 'mean',
		label: 'Mean',
		description: 'Tracks the average of all values in a column.',
		iconName: 'hash',
		fill: 'icon/emphasis/default',
		group: 'column',
		canSchedule: true,
		isGrouped: false,
	},
	min: {
		value: 'min',
		label: 'Minimum',
		description: 'Tracks the minimum of all values in a column.',
		iconName: 'hash',
		fill: 'icon/emphasis/default',
		group: 'column',
		canSchedule: true,
		isGrouped: false,
	},
	null_percentage: {
		value: 'null_percentage',
		label: 'Null percentage',
		description: 'Tracks the percentage of values in a column that are null.',
		iconName: 'columns2',
		fill: 'icon/emphasis/default',
		group: 'column',
		canSchedule: true,
		isGrouped: false,
	},
	unique_percentage: {
		value: 'unique_percentage',
		label: 'Unique percentage',
		description: 'Tracks the percentage of values in a column that are unique.',
		iconName: 'columns2',
		fill: 'icon/emphasis/default',
		group: 'column',
		canSchedule: true,
		isGrouped: false,
	},
	custom_sql: {
		value: 'custom_sql',
		label: 'Custom SQL',
		description: 'Define a monitor using custom SQL.',
		iconName: 'code',
		fill: 'icon/emphasis/default',
		group: 'custom',
		canSchedule: true,
		isGrouped: false,
	},
	analytics: {
		label: 'Analytics',
		description: 'Tracks the specific integration analytics',
		iconName: 'chartBar',
		fill: 'icon/emphasis/default',
		value: 'analytics',
		group: 'analytics',
		canSchedule: false,
		isGrouped: false,
	},
	job_success_rate: {
		value: 'job_success_rate',
		label: 'Job Success Rate',
		description: 'Tracks the percentage of successful job executions.',
		iconName: 'circleCheck',
		fill: 'icon/emphasis/default',
		group: 'job',
		canSchedule: false,
		isGrouped: true,
	},
	job_error_rate: {
		value: 'job_error_rate',
		label: 'Job Error Rate',
		description: 'Tracks the percentage of failed job executions.',
		iconName: 'hash',
		fill: 'icon/emphasis/default',
		group: 'job',
		canSchedule: false,
		isGrouped: true,
	},
	job_duration: {
		value: 'job_duration',
		label: 'Job Duration',
		description: 'Measures the time taken for job completion.',
		iconName: 'clockPlay',
		fill: 'icon/emphasis/default',
		group: 'job',
		canSchedule: false,
		isGrouped: false,
	},
};
